import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Eye, Download } from "lucide-react";
import { InvoiceBuilder } from "@/components/invoices/InvoiceBuilder";
import { InvoiceViewDialog } from "@/components/invoices/InvoiceViewDialog";
import { InvoiceStatusDropdown } from "@/components/invoices/InvoiceStatusDropdown";
import { format } from "date-fns";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { useLanguage } from "@/hooks/use-language";
import { useToast } from "@/hooks/use-toast";

export default function Invoices() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      en: {
        title: "Invoices",
        subtitle: "Create and manage client invoices",
        createInvoice: "Create Invoice",
        client: "Client",
        issued: "Issued",
        due: "Due",
        amount: "Amount",
        status: "Status",
        actions: "Actions",
        view: "View",
        download: "Download",
        noInvoices: "No invoices yet",
        noInvoicesMsg: "Create your first invoice to get started",
      },
      fr: {
        title: "Factures",
        subtitle: "Créer et gérer les factures clients",
        createInvoice: "Créer une Facture",
        client: "Client",
        issued: "Émise",
        due: "Échéance",
        amount: "Montant",
        status: "Statut",
        actions: "Actions",
        view: "Voir",
        download: "Télécharger",
        noInvoices: "Aucune facture",
        noInvoicesMsg: "Créez votre première facture pour commencer",
      },
      ar: {
        title: "الفواتير",
        subtitle: "إنشاء وإدارة فواتير العملاء",
        createInvoice: "إنشاء فاتورة",
        client: "العميل",
        issued: "تاريخ الإصدار",
        due: "تاريخ الاستحقاق",
        amount: "المبلغ",
        status: "الحالة",
        actions: "الإجراءات",
        view: "عرض",
        download: "تحميل",
        noInvoices: "لا توجد فواتير بعد",
        noInvoicesMsg: "أنشئ فاتورتك الأولى للبدء",
      },
      it: {
        title: "Fatture",
        subtitle: "Crea e gestisci fatture clienti",
        createInvoice: "Crea Fattura",
        client: "Cliente",
        issued: "Emessa",
        due: "Scadenza",
        amount: "Importo",
        status: "Stato",
        actions: "Azioni",
        view: "Visualizza",
        download: "Scarica",
        noInvoices: "Nessuna fattura",
        noInvoicesMsg: "Crea la tua prima fattura per iniziare",
      },
    };
    return translations[language]?.[key] || translations.en[key];
  };

  const { data: invoices, isLoading, refetch } = useQuery({
    queryKey: ["invoices"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("invoices")
        .select(`
          *,
          invoice_items (*)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Real-time subscription
  useRealtimeSubscription("invoices", "invoices");

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  };


  const handleDownloadInvoice = (invoice: any) => {
    // Create invoice HTML for direct download
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; background: white; color: black; }
            .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { color: #667eea; font-size: 32px; margin-bottom: 10px; }
            .grid { display: flex; gap: 40px; margin-bottom: 40px; }
            .grid > div { flex: 1; }
            .section-title { font-size: 16px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 10px; }
            .details { font-size: 14px; line-height: 1.8; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .text-right { text-align: right; }
            .totals { margin-top: 30px; display: flex; justify-content: flex-end; }
            .totals-box { width: 300px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd; }
            .totals-total { display: flex; justify-content: space-between; padding: 15px 0; border-top: 2px solid #333; margin-top: 10px; font-size: 20px; font-weight: bold; color: #667eea; }
            .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INVOICE</h1>
            <p>Spotlight ADZ - AI-Powered Marketing Agency</p>
          </div>
          <div class="grid">
            <div>
              <h3 class="section-title">Invoice Details</h3>
              <div class="details">
                <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
                <p><strong>Issue Date:</strong> ${format(new Date(invoice.issue_date), "MMMM dd, yyyy")}</p>
                <p><strong>Due Date:</strong> ${format(new Date(invoice.due_date), "MMMM dd, yyyy")}</p>
                <p><strong>Status:</strong> ${invoice.status}</p>
              </div>
            </div>
            <div>
              <h3 class="section-title">Bill To</h3>
              <div class="details">
                <p><strong>${invoice.client_name}</strong></p>
                <p>${invoice.client_email}</p>
                ${invoice.client_address ? `<p>${invoice.client_address}</p>` : ''}
              </div>
            </div>
          </div>
          <h3 class="section-title">Items</h3>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.invoice_items?.map((item: any) => `
                <tr>
                  <td>${item.description}</td>
                  <td class="text-right">${item.quantity}</td>
                  <td class="text-right">${invoice.currency} ${Number(item.unit_price).toFixed(2)}</td>
                  <td class="text-right">${invoice.currency} ${Number(item.amount).toFixed(2)}</td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>
          <div class="totals">
            <div class="totals-box">
              <div class="totals-row">
                <span>Subtotal:</span>
                <span>${invoice.currency} ${Number(invoice.subtotal).toFixed(2)}</span>
              </div>
              ${invoice.tax_rate > 0 ? `
                <div class="totals-row">
                  <span>Tax (${invoice.tax_rate}%):</span>
                  <span>${invoice.currency} ${Number(invoice.tax_amount).toFixed(2)}</span>
                </div>
              ` : ''}
              ${invoice.discount_amount > 0 ? `
                <div class="totals-row" style="color: green;">
                  <span>Discount:</span>
                  <span>-${invoice.currency} ${Number(invoice.discount_amount).toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="totals-total">
                <span>Total:</span>
                <span>${invoice.currency} ${Number(invoice.total_amount).toFixed(2)}</span>
              </div>
            </div>
          </div>
          ${invoice.payment_instructions ? `
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              <h3 class="section-title">Payment Instructions</h3>
              <p class="details">${invoice.payment_instructions}</p>
            </div>
          ` : ''}
          ${invoice.terms || invoice.notes ? `
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              ${invoice.terms ? `
                <div style="margin-bottom: 20px;">
                  <h3 style="font-weight: bold; margin-bottom: 10px;">Terms & Conditions</h3>
                  <p class="details">${invoice.terms}</p>
                </div>
              ` : ''}
              ${invoice.notes ? `
                <div>
                  <h3 style="font-weight: bold; margin-bottom: 10px;">Notes</h3>
                  <p class="details">${invoice.notes}</p>
                </div>
              ` : ''}
            </div>
          ` : ''}
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>© ${new Date().getFullYear()} Spotlight ADZ. All rights reserved.</p>
          </div>
        </body>
      </html>
    `;

    // Open print dialog for PDF save
    const printWindow = window.open('', '', 'height=800,width=800');
    if (printWindow) {
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };


  return (
    <>
      <InvoiceViewDialog 
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        invoice={selectedInvoice}
      />
      <div className="animate-fade-in space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t("title")}</h1>
          <p className="text-muted-foreground mt-2">{t("subtitle")}</p>
        </div>
        <Button onClick={() => setBuilderOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("createInvoice")}
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
        </div>
      ) : invoices && invoices.length > 0 ? (
        <div className="grid gap-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="p-6">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">{invoice.invoice_number}</h3>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p><span className="font-medium">{t("client")}:</span> {invoice.client_name}</p>
                    <p><span className="font-medium">{t("issued")}:</span> {format(new Date(invoice.issue_date), "MMM dd, yyyy")}</p>
                    <p><span className="font-medium">{t("due")}:</span> {format(new Date(invoice.due_date), "MMM dd, yyyy")}</p>
                    <p className="text-xl font-bold text-foreground mt-2">
                      {invoice.currency} {invoice.total_amount.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 min-w-[140px]">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">{t("status")}</p>
                    <InvoiceStatusDropdown 
                      invoiceId={invoice.id}
                      currentStatus={invoice.status}
                      invoiceNumber={invoice.invoice_number}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">{t("actions")}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleViewInvoice(invoice)}>
                        <Eye className="h-4 w-4 mr-1" />
                        {t("view")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadInvoice(invoice)}>
                        <Download className="h-4 w-4 mr-1" />
                        {t("download")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12">
          <div className="text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">{t("noInvoices")}</p>
            <p className="text-sm text-muted-foreground mt-2">{t("noInvoicesMsg")}</p>
          </div>
        </Card>
      )}

      <InvoiceBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        onSuccess={() => refetch()}
      />
    </div>
    </>
  );
}
